import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { roundTo2 } from '../../miscelaneo/monetario';
import { calcularDistribucion } from '../../miscelaneo/aplicacionPago';
import PlantillaModal from '../util/PlantillaModal';

const METODOS_PAGO = [
  { id: 1, label: 'Efectivo' },
  { id: 2, label: 'Transferencia / Depósito' },
  { id: 3, label: 'Tarjeta Crédito/Débito' },
  { id: 4, label: 'Cheque' }
];
const SIMBOLO_MONEDA = { 1: 'RD$', 2: 'US$', 3: '€' };
const NOMBRE_MONEDA = { 1: 'Peso Dominicano', 2: 'Dólar', 3: 'Euro' };

const ENCABEZADO = 'px-3 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider';
const INPUT = 'w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors';

function formatMonto(monto) {
  return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Aplicar un pago a la cuenta de un cliente: el monto se reparte de la factura mas vieja a la mas nueva,
 * salvo los montos especificos que el usuario fije en facturas concretas.
 */
export default function ModalAplicarPago({ mostrar, onClose, cliente, onPagoAplicado }) {
  const [pendientes, setPendientes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [monto, setMonto] = useState('');
  const [dirigidos, setDirigidos] = useState({});
  const [idMoneda, setIdMoneda] = useState(null);
  const [idMetodo, setIdMetodo] = useState(1);
  const [idCuenta, setIdCuenta] = useState('');
  const [referencia, setReferencia] = useState('');
  const [cheque, setCheque] = useState({ banco: '', numero: '', titular: '', fechaCobro: '' });
  const [cuentas, setCuentas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargarPendientes = useCallback(async () => {
    if (!cliente?.id_entidad_cliente) return;
    try {
      setCargando(true);
      const { data } = await instanciaAxios.get(`/cxc/cliente/${cliente.id_entidad_cliente}/facturas-pendientes`);
      setPendientes(data.facturas);
      setIdMoneda(prev => (data.facturas.some(f => f.id_moneda === prev) ? prev : data.facturas[0]?.id_moneda ?? null));
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar las facturas pendientes');
    } finally {
      setCargando(false);
    }
  }, [cliente?.id_entidad_cliente]);

  useEffect(() => {
    if (!mostrar) return;
    setMonto('');
    setDirigidos({});
    setIdMetodo(1);
    setIdCuenta('');
    setReferencia('');
    setCheque({ banco: '', numero: '', titular: '', fechaCobro: '' });
    setResultado(null);
    cargarPendientes();
    Promise.all([instanciaAxios.get('/cuenta_tesoreria/resumida'), instanciaAxios.get('/banco')])
      .then(([c, b]) => { setCuentas(c.data || []); setBancos(b.data || []); })
      .catch(() => toast.error('Error al cargar cuentas de tesorería'));
  }, [mostrar, cargarPendientes]);

  useEffect(() => { setIdCuenta(''); }, [idMetodo, idMoneda]);

  const monedas = useMemo(() => [...new Set(pendientes.map(f => f.id_moneda))], [pendientes]);
  // Solo se aplican facturas de la moneda del pago
  const elegibles = useMemo(() => pendientes.filter(f => f.id_moneda === idMoneda), [pendientes, idMoneda]);
  const totalPendiente = roundTo2(elegibles.reduce((s, f) => s + f.balance, 0));
  const simbolo = SIMBOLO_MONEDA[idMoneda] || '';

  // Efectivo se deposita en una caja (tipo 1); el resto en una cuenta bancaria (tipo 2), siempre en la moneda del pago
  const cuentasDisponibles = useMemo(
    () => cuentas.filter(c => c.id_tipo_cuenta_tesoreria === (idMetodo === 1 ? 1 : 2) && c.id_moneda === idMoneda),
    [cuentas, idMetodo, idMoneda]
  );

  const sumaDirigidos = roundTo2(Object.values(dirigidos).reduce((s, v) => s + (parseFloat(v) > 0 ? roundTo2(v) : 0), 0));
  // Si no se escribe un monto total, el pago es la suma de los montos especificos
  const montoEfectivo = monto !== '' ? roundTo2(monto) : sumaDirigidos;
  const dist = useMemo(() => calcularDistribucion(elegibles, montoEfectivo, dirigidos), [elegibles, montoEfectivo, dirigidos]);

  const listo = !cargando && montoEfectivo > 0 && !dist.error && dist.aplicaciones.length > 0;

  const confirmar = async () => {
    if (!listo || procesando) return;
    if (!idCuenta) return toast.error('Seleccione la cuenta de tesorería');
    if (idMetodo === 4 && (!cheque.banco || !cheque.numero || !cheque.fechaCobro)) {
      return toast.error('Complete banco, número y fecha de cobro del cheque');
    }
    try {
      setProcesando(true);
      const { data } = await instanciaAxios.post(`/cxc/cliente/${cliente.id_entidad_cliente}/aplicar-pago`, {
        monto: montoEfectivo,
        id_metodo_pago: idMetodo,
        id_cuenta_tesoreria: parseInt(idCuenta),
        asignaciones: Object.entries(dirigidos)
          .filter(([, v]) => v !== '' && parseFloat(v) > 0)
          .map(([id, v]) => ({ id_factura: parseInt(id), monto: roundTo2(v) })),
        // Lo que el usuario vio: el servidor lo recalcula con los balances reales y rechaza si difiere
        distribucion_esperada: dist.aplicaciones.map(a => ({ id_factura: a.id_factura, monto: a.monto_aplicado })),
        referencia: referencia || null,
        id_banco_origen: idMetodo === 4 && cheque.banco ? parseInt(cheque.banco) : null,
        numero_cheque: idMetodo === 4 ? cheque.numero : null,
        nombre_titular_cheque: idMetodo === 4 ? cheque.titular || null : null,
        fecha_cobro_cheque: idMetodo === 4 ? cheque.fechaCobro : null
      });
      setResultado({ ...data, filas: dist.aplicaciones });
      toast.success('Pago aplicado a la cuenta del cliente');
      onPagoAplicado();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      const codigo = error.response?.data?.codigo;
      toast.error(error.response?.data?.mensaje || 'Error al aplicar el pago');
      // Balances cambiados (otro usuario cobró, o doble envío): refrescar para revisar el nuevo detalle
      if (codigo === 'DISTRIBUCION_CAMBIO' || codigo === 'CONFLICTO_CONCURRENCIA') {
        cargarPendientes();
        onPagoAplicado();
      }
    } finally {
      setProcesando(false);
    }
  };

  const aplicacionPorId = new Map(dist.aplicaciones.map(a => [a.id_factura, a]));

  return (
    <PlantillaModal mostrar={mostrar} onClose={onClose} titulo={resultado ? 'Pago aplicado' : 'Aplicar pago a la cuenta'} maxWidth="max-w-6xl">
      <div className="space-y-4">
        {resultado ? (
          <>
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-green-800">
              <FiCheckCircle size={20} />
              <span className="font-medium">
                Se aplicaron {simbolo} {formatMonto(resultado.total_aplicado)}. Balance del cliente: {simbolo} {formatMonto(resultado.balance_cliente_actual)}
              </span>
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`${ENCABEZADO} text-left`}>Factura</th>
                    <th className={`${ENCABEZADO} text-left`}>Fecha</th>
                    <th className={`${ENCABEZADO} text-right`}>Balance anterior</th>
                    <th className={`${ENCABEZADO} text-right`}>Aplicado</th>
                    <th className={`${ENCABEZADO} text-right`}>Queda</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {resultado.filas.map(f => (
                    <tr key={f.id_factura}>
                      <td className="px-3 py-2 text-sm font-medium">{f.ncf || `Factura #${f.id_factura}`}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatFecha(f.fecha_hora_creacion)}</td>
                      <td className="px-3 py-2 text-sm text-right">{simbolo} {formatMonto(f.balance)}</td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-green-700">{simbolo} {formatMonto(f.monto_aplicado)}</td>
                      <td className="px-3 py-2 text-sm text-right">
                        {f.balance_posterior === 0 ? <span className="text-green-700 font-medium">Saldada</span> : `${simbolo} ${formatMonto(f.balance_posterior)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 border border-gray-400 rounded-xl hover:bg-gray-100">Cerrar</button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
              <span className="text-gray-600">Balance pendiente de {cliente.razon_social}</span>
              <span className="text-lg font-bold text-color-primario">{simbolo} {formatMonto(totalPendiente)}</span>
            </div>

            {!cargando && pendientes.length === 0 && (
              <p className="text-center text-gray-500 py-6">El cliente no tiene facturas pendientes.</p>
            )}

            {pendientes.length > 0 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {monedas.length > 1 && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Moneda del pago</label>
                      <select value={idMoneda ?? ''} onChange={(e) => { setIdMoneda(parseInt(e.target.value)); setDirigidos({}); setMonto(''); }} className={INPUT}>
                        {monedas.map(m => <option key={m} value={m}>{NOMBRE_MONEDA[m] || `Moneda ${m}`}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Monto total del pago</label>
                    <div className="flex gap-2">
                      <input type="number" min="0" step="0.01" value={monto} autoFocus
                        onChange={(e) => setMonto(e.target.value)} className={INPUT}
                        placeholder={sumaDirigidos > 0 ? formatMonto(sumaDirigidos) : '0.00'} />
                      <button type="button" onClick={() => setMonto(totalPendiente.toFixed(2))}
                        className="px-3 py-2 text-sm border border-gray-400 rounded-lg hover:bg-gray-100 whitespace-nowrap">
                        Saldar todo
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Método de pago</label>
                    <select value={idMetodo} onChange={(e) => setIdMetodo(parseInt(e.target.value))} className={INPUT}>
                      {METODOS_PAGO.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{idMetodo === 1 ? 'Caja destino' : 'Cuenta destino'}</label>
                    <select value={idCuenta} onChange={(e) => setIdCuenta(e.target.value)} className={INPUT}>
                      <option value="">Seleccionar...</option>
                      {cuentasDisponibles.map(c => (
                        <option key={c.id_cuenta_tesoreria} value={c.id_cuenta_tesoreria}>{c.descripcion}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Referencia (opcional)</label>
                    <input type="text" value={referencia} onChange={(e) => setReferencia(e.target.value)} className={INPUT} />
                  </div>
                  {idMetodo === 4 && (
                    <>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Banco emisor</label>
                        <select value={cheque.banco} onChange={(e) => setCheque({ ...cheque, banco: e.target.value })} className={INPUT}>
                          <option value="">Seleccionar banco</option>
                          {bancos.map(b => <option key={b.id_banco} value={b.id_banco}>{b.descripcion}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Número de cheque</label>
                        <input type="text" value={cheque.numero} onChange={(e) => setCheque({ ...cheque, numero: e.target.value })} className={INPUT} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Titular (opcional)</label>
                        <input type="text" value={cheque.titular} onChange={(e) => setCheque({ ...cheque, titular: e.target.value })} className={INPUT} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Fecha de cobro</label>
                        <input type="date" value={cheque.fechaCobro} onChange={(e) => setCheque({ ...cheque, fechaCobro: e.target.value })} className={INPUT} />
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <h3 className="font-bold text-gray-800">Distribución del pago</h3>
                  <p className="text-xs text-gray-500 mb-2">
                    Escriba un monto específico para dirigir dinero a una factura concreta. El resto del pago se reparte
                    automáticamente de la factura más vieja a la más nueva, sin tocar las facturas con monto específico.
                  </p>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`${ENCABEZADO} text-left`}>Factura</th>
                          <th className={`${ENCABEZADO} text-left`}>Fecha</th>
                          <th className={`${ENCABEZADO} text-right`}>Balance actual</th>
                          <th className={`${ENCABEZADO} text-right w-40`}>Monto específico</th>
                          <th className={`${ENCABEZADO} text-right`}>Se aplica</th>
                          <th className={`${ENCABEZADO} text-right`}>Queda</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {elegibles.map(f => {
                          const ap = aplicacionPorId.get(f.id_factura);
                          const errorFila = dist.facturaConError === f.id_factura;
                          return (
                            <tr key={f.id_factura} className={ap ? 'bg-green-50/40' : ''}>
                              <td className="px-3 py-2 text-sm font-medium text-gray-900">{f.ncf || `Factura #${f.id_factura}`}</td>
                              <td className="px-3 py-2 text-sm text-gray-600">{formatFecha(f.fecha_hora_creacion)}</td>
                              <td className="px-3 py-2 text-sm text-right">{simbolo} {formatMonto(f.balance)}</td>
                              <td className="px-3 py-1">
                                <input type="number" min="0" step="0.01" max={f.balance} value={dirigidos[f.id_factura] ?? ''}
                                  onChange={(e) => setDirigidos(prev => ({ ...prev, [f.id_factura]: e.target.value }))}
                                  placeholder="Auto"
                                  className={`w-full px-2 py-1 text-sm text-right border rounded-lg ${errorFila ? 'border-red-500' : 'border-gray-300'}`} />
                              </td>
                              <td className="px-3 py-2 text-sm text-right font-semibold text-green-700">
                                {ap ? `${simbolo} ${formatMonto(ap.monto_aplicado)}` : '—'}
                                {ap && <span className="ml-1 text-xs font-normal text-gray-500">({ap.origen === 'dirigido' ? 'específico' : 'auto'})</span>}
                              </td>
                              <td className="px-3 py-2 text-sm text-right">
                                {!ap ? '—' : ap.balance_posterior === 0
                                  ? <span className="text-green-700 font-medium">Saldada</span>
                                  : `${simbolo} ${formatMonto(ap.balance_posterior)}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={4} className="px-3 py-2 text-sm font-bold text-right">Total aplicado</td>
                          <td className="px-3 py-2 text-sm font-bold text-right">
                            {simbolo} {formatMonto(dist.aplicaciones.reduce((s, a) => s + a.monto_aplicado, 0))}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {dist.error && (
                    <p className="flex items-center gap-1 text-sm text-red-600 mt-2"><FiAlertCircle /> {dist.error}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 border border-gray-400 rounded-xl hover:bg-gray-100">Cancelar</button>
              <button onClick={confirmar} disabled={!listo || procesando}
                className="px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
                {procesando ? 'Aplicando...' : `Aplicar pago de ${simbolo} ${formatMonto(montoEfectivo)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </PlantillaModal>
  );
}
